import { useEffect, useState } from "react";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import "./admin.css";
import "./ManagePriceOverrides.css";

const EMPTY_FORM = {
  label: "",
  startDate: "",
  endDate: "",
};

/* ─── tiny helper ─── */
const getBikeImageUrl = (image) => {
  if (!image) return null;
  const clean = image.trim();
  if (clean.startsWith("http://") || clean.startsWith("https://")) return clean;
  return `https://bros-bike-rental-api.onrender.com/${clean.replace(/^\/+/, "")}`;
};

function ManagePriceOverrides() {
  const [overrides, setOverrides]   = useState([]);
  const [bikes, setBikes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [editGroup, setEditGroup]   = useState(null); // { label, startDate, endDate, overrides[] }
  const [form, setForm]             = useState(EMPTY_FORM);
  /* bikePrice: { [bikeId]: priceString } */
  const [bikePrice, setBikePrice]   = useState({});
  const [saving, setSaving]         = useState(false);

  const token       = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  /* ── fetch all overrides ── */
  const fetchOverrides = async () => {
    try {
      const res = await axiosClient.get("/price-overrides", authHeaders);
      setOverrides(res.data.overrides || []);
    } catch {
      toast.error("Failed to load price overrides");
    }
  };

  /* ── fetch bikes ── */
  const fetchBikes = async () => {
    try {
      const res = await axiosClient.get("/admin/bikes", authHeaders);
      setBikes(res.data.bikes || res.data || []);
    } catch {
      try {
        const pub = await axiosClient.get("/bikes");
        setBikes(pub.data || []);
      } catch {
        console.warn("Could not load bikes");
      }
    }
  };

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchOverrides(), fetchBikes()]);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  /* ── open blank create form ── */
  const openCreate = () => {
    setEditGroup(null);
    setForm(EMPTY_FORM);
    setBikePrice({});
    setShowForm(true);
  };

  /* ─────────────────────────────────────────────────────────────────
     Group overrides by label+startDate+endDate so the table shows
     one row per "event" with per-bike chips instead of per-override rows
  ───────────────────────────────────────────────────────────────── */
  const groupedOverrides = Object.values(
    overrides.reduce((acc, o) => {
      const key = `${o.label}||${o.startDate}||${o.endDate}`;
      if (!acc[key]) {
        acc[key] = {
          key,
          label: o.label,
          startDate: o.startDate,
          endDate: o.endDate,
          isActive: o.isActive,
          items: [],
        };
      }
      acc[key].items.push(o);
      return acc;
    }, {})
  );

  /* ── open edit form for an entire event group ── */
  const openEdit = (group) => {
    setEditGroup(group);
    setForm({
      label:     group.label,
      startDate: group.startDate.slice(0, 10),
      endDate:   group.endDate.slice(0, 10),
    });
    const prices = {};
    group.items.forEach((o) => {
      const id = o.bikeId?._id || o.bikeId;
      if (id) prices[id] = String(o.pricePerDay);
    });
    setBikePrice(prices);
    setShowForm(true);
  };

  /* ── submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.label || !form.startDate || !form.endDate) {
      return toast.warning("Label and both dates are required.");
    }
    if (new Date(form.endDate) <= new Date(form.startDate)) {
      return toast.error("End date must be after start date.");
    }

    const bikesWithPrice = bikes.filter(
      (b) => bikePrice[b._id] && Number(bikePrice[b._id]) > 0
    );
    if (bikesWithPrice.length === 0) {
      return toast.warning("Please enter a special price for at least one bike.");
    }

    setSaving(true);
    try {
      if (editGroup) {
        /* ── UPDATE MODE ──
           For each bike that had a price before:
             - if still present → update
             - if removed       → delete
           For new bikes in this submission → create
        */
        const oldById = {};
        editGroup.items.forEach((o) => {
          const id = o.bikeId?._id || o.bikeId;
          if (id) oldById[String(id)] = o;
        });

        const promises = [];

        // Update / delete existing
        Object.entries(oldById).forEach(([id, o]) => {
          if (bikePrice[id] && Number(bikePrice[id]) > 0) {
            // update
            promises.push(
              axiosClient.put(
                `/price-overrides/${o._id}`,
                {
                  label:      form.label,
                  startDate:  form.startDate,
                  endDate:    form.endDate,
                  pricePerDay: Number(bikePrice[id]),
                  bikeId:     id,
                },
                authHeaders
              )
            );
          } else {
            // bike price cleared → delete this override
            promises.push(
              axiosClient.delete(`/price-overrides/${o._id}`, authHeaders)
            );
          }
        });

        // Create new ones (bikes not in oldById but now have a price)
        bikesWithPrice.forEach((b) => {
          if (!oldById[b._id]) {
            promises.push(
              axiosClient.post(
                "/price-overrides",
                {
                  label:      form.label,
                  startDate:  form.startDate,
                  endDate:    form.endDate,
                  pricePerDay: Number(bikePrice[b._id]),
                  bikeId:     b._id,
                },
                authHeaders
              )
            );
          }
        });

        await Promise.all(promises);
        toast.success("Price override updated ✅");
      } else {
        /* ── CREATE MODE — one override per bike ── */
        await Promise.all(
          bikesWithPrice.map((b) =>
            axiosClient.post(
              "/price-overrides",
              {
                label:      form.label,
                startDate:  form.startDate,
                endDate:    form.endDate,
                pricePerDay: Number(bikePrice[b._id]),
                bikeId:     b._id,
              },
              authHeaders
            )
          )
        );
        toast.success("Price override created ✅");
      }

      setShowForm(false);
      setEditGroup(null);
      fetchOverrides();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save override");
    } finally {
      setSaving(false);
    }
  };

  /* ── toggle active for all overrides in a group ── */
  const handleToggle = async (group) => {
    const newStatus = !group.isActive;
    try {
      await Promise.all(
        group.items.map((o) =>
          axiosClient.put(`/price-overrides/${o._id}`, { isActive: newStatus }, authHeaders)
        )
      );
      toast.success(`Override ${newStatus ? "activated" : "deactivated"}`);
      fetchOverrides();
    } catch {
      toast.error("Failed to update override");
    }
  };

  /* ── delete entire group ── */
  const handleDelete = async (group) => {
    const result = await Swal.fire({
      title:             "Delete this override?",
      text:              "All per-bike prices for this event will be removed.",
      icon:              "warning",
      showCancelButton:  true,
      confirmButtonColor: "var(--danger)",
      confirmButtonText: "Yes, delete",
      background:        "var(--surface)",
      color:             "var(--text)",
    });
    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        group.items.map((o) =>
          axiosClient.delete(`/price-overrides/${o._id}`, authHeaders)
        )
      );
      toast.success("Override deleted");
      fetchOverrides();
    } catch {
      toast.error("Failed to delete override");
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });

  return (
    <div className="po-page">
      {/* ── HEADER ── */}
      <div className="po-header-row">
        <div className="admin-page-header">
          <h1>Price Overrides</h1>
          <p>Configure custom surge prices for specific bikes or holidays</p>
        </div>
        <button className="btn btn-primary po-btn-add" onClick={openCreate}>
          + Add Override
        </button>
      </div>

      {/* ── MODAL FORM ── */}
      {showForm && (
        <div className="po-backdrop" onClick={() => setShowForm(false)}>
          <div className="po-modal po-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="po-modal-header">
              <h2>{editGroup ? "Edit Price Override" : "New Price Override"}</h2>
              <button
                className="po-modal-close"
                onClick={() => setShowForm(false)}
                title="Close"
              >
                ✕
              </button>
            </div>

            <form className="po-form" onSubmit={handleSubmit}>
              {/* Label */}
              <div className="po-field">
                <label>Label / Event Name</label>
                <input
                  type="text"
                  placeholder="e.g. Weekend Surge, Diwali Special"
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                />
              </div>

              {/* Dates */}
              <div className="po-field-row">
                <div className="po-field">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  />
                </div>
                <div className="po-field">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={form.endDate}
                    min={form.startDate}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  />
                </div>
              </div>

              {/* ── Per-Bike Price Grid ── */}
              <div className="po-field">
                <label>Special Price Per Bike</label>
                <p className="po-field-hint" style={{ marginBottom: "12px" }}>
                  Enter a custom price for each bike you want to override. Leave blank to keep the bike's default price.
                </p>

                {bikes.length === 0 ? (
                  <p className="po-field-hint">Loading bikes…</p>
                ) : (
                  <div className="po-bike-grid">
                    {bikes.map((bike) => {
                      const imgSrc = getBikeImageUrl(bike.image);
                      const price  = bikePrice[bike._id] ?? "";
                      const changed = price !== "" && Number(price) !== bike.pricePerDay;

                      return (
                        <div
                          key={bike._id}
                          className={`po-bike-card ${price ? "po-bike-card--active" : ""}`}
                        >
                          {imgSrc && (
                            <img
                              src={imgSrc}
                              alt={bike.name}
                              className="po-bike-card-img"
                            />
                          )}
                          <div className="po-bike-card-info">
                            <div className="po-bike-card-name">{bike.name}</div>
                            <div className="po-bike-card-meta">
                              <span className="po-bike-type-badge">{bike.type}</span>
                              <span className="po-bike-default-price">
                                Default: ₹{bike.pricePerDay}/day
                              </span>
                            </div>
                          </div>
                          <div className="po-bike-price-input-wrap">
                            <span className="po-rupee-symbol">₹</span>
                            <input
                              type="number"
                              min="1"
                              placeholder={bike.pricePerDay}
                              value={price}
                              className="po-bike-price-input"
                              onChange={(e) =>
                                setBikePrice((prev) => ({
                                  ...prev,
                                  [bike._id]: e.target.value,
                                }))
                              }
                            />
                            <span className="po-per-day">/day</span>
                          </div>
                          {changed && (
                            <div className="po-bike-card-preview">
                              Customers see ₹{price}{" "}
                              <s style={{ opacity: 0.6 }}>
                                ₹{Math.round(Number(price) * 1.4)}
                              </s>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="po-form-actions">
                <button
                  type="button"
                  className="po-btn-cancel"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="po-btn-save"
                  disabled={saving}
                >
                  {saving ? "Saving…" : editGroup ? "Update Override" : "Create Override"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── TABLE / STATES ── */}
      {loading ? (
        <div className="admin-skel-wrap">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="admin-skel-row"
              style={{ animationDelay: `${i * 0.08}s` }}
            />
          ))}
        </div>
      ) : groupedOverrides.length === 0 ? (
        <div className="admin-empty">
          <div className="empty-icon">🏷️</div>
          <p>No price overrides set yet.</p>
          <p style={{ marginTop: "6px", fontSize: "13px" }}>
            Click <strong>+ Add Override</strong> to create custom surge prices for specific bikes.
          </p>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Bikes & Prices</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupedOverrides.map((group) => (
                <tr key={group.key} className={!group.isActive ? "po-row-inactive" : ""}>
                  <td className="po-label-cell">
                    <span className="po-label-tag">🏷️</span>
                    {group.label}
                  </td>
                  <td>
                    <div className="po-bike-chips">
                      {group.items.map((o) => {
                        const bike = o.bikeId;
                        return (
                          <span key={o._id} className="po-bike-chip">
                            {bike?.name || "—"}{" "}
                            <strong>₹{o.pricePerDay}</strong>
                          </span>
                        );
                      })}
                    </div>
                  </td>
                  <td>{formatDate(group.startDate)}</td>
                  <td>{formatDate(group.endDate)}</td>
                  <td>
                    <button
                      type="button"
                      className={`badge ${group.isActive ? "badge-green" : "badge-red"}`}
                      onClick={() => handleToggle(group)}
                      title="Click to toggle status"
                      style={{ cursor: "pointer", border: "none" }}
                    >
                      {group.isActive ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td>
                    <div className="po-action-btns">
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => openEdit(group)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(group)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ManagePriceOverrides;
